import React, { useState } from 'react';
import axios from 'axios';

const METRICS = [
  {
    category: 'Purchase Behavior',
    items: [
      { value: 'purchases_by_price',   label: 'Purchases by Price Point' },
      { value: 'return_rate_by_price', label: 'Return Rate by Price' },
      { value: 'purchase_velocity',    label: 'Purchase Velocity (Time to First Sale)' },
      { value: 'price_elasticity',     label: 'Price Elasticity (Purchase Rate vs Price)' },
      { value: 'unsold_by_price',      label: 'Unsold Articles by Price' },
    ],
  },
  {
    category: 'Genre & Content',
    items: [
      { value: 'revenue_by_genre',    label: 'Revenue by Genre' },
      { value: 'genre_price_matrix',  label: 'Purchases by Genre × Price' },
      { value: 'sweet_spot',          label: 'Price Sweet Spot by Genre' },
    ],
  },
  {
    category: 'User Behavior',
    items: [
      { value: 'user_spend',            label: 'User Spend Distribution' },
      { value: 'avg_articles_per_user', label: 'Avg Articles Purchased per User / Month' },
    ],
  },
  {
    category: 'Publisher Performance',
    items: [
      { value: 'company_price_perf', label: 'Publisher Price Performance' },
    ],
  },
  {
    category: 'Pricing Mode',
    items: [
      { value: 'auto_vs_manual', label: 'Auto vs Manual Pricing Revenue' },
    ],
  },
  {
    category: 'Geographic',
    items: [
      { value: 'geo_by_state',  label: 'Purchases by State' },
      { value: 'geo_avg_spend', label: 'Avg Spend by State' },
      { value: 'geo_genre',     label: 'Genre Popularity by State' },
      { value: 'geo_by_zip',    label: 'Top ZIP Codes by Purchases' },
    ],
  },
];

// Derive the max value of a given column index in raw for bar scaling
function maxForKey(raw, barKey) {
  if (!raw || !raw.length || !barKey) return 1;
  const vals = raw.map(r => parseFloat(r[barKey]) || 0);
  return Math.max(...vals) || 1;
}

export default function AdminAnalytics({ token }) {
  const [metric, setMetric]     = useState('');
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [sortCol, setSortCol]   = useState(null);
  const [sortAsc, setSortAsc]   = useState(false);

  const authHeader = { Authorization: `Bearer ${token}` };

  async function load() {
    if (!metric) return;
    setLoading(true);
    setError('');
    setData(null);
    setSortCol(null);
    try {
      const res = await axios.get(`/api/admin/analytics?metric=${metric}`, { headers: authHeader });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }

  function toggleSort(colIdx) {
    if (sortCol === colIdx) setSortAsc(a => !a);
    else { setSortCol(colIdx); setSortAsc(false); }
  }

  function sortedRows() {
    if (!data || sortCol === null) return data?.rows || [];
    return [...data.rows].sort((a, b) => {
      const av = isNaN(a[sortCol]) ? a[sortCol] : parseFloat(a[sortCol]);
      const bv = isNaN(b[sortCol]) ? b[sortCol] : parseFloat(b[sortCol]);
      return sortAsc ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
  }

  // Determine which column index corresponds to barKey
  function barColIdx() {
    if (!data?.barKey || !data?.raw?.length) return null;
    const k = data.barKey;
    const keys = Object.keys(data.raw[0]);
    return keys.indexOf(k);
  }

  const rows   = sortedRows();
  const barIdx = barColIdx();
  const maxVal = data ? maxForKey(data.raw, data.barKey) : 1;

  const card   = { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
  const btnPrimary = { padding: '0.55rem 1.4rem', background: '#0d3165', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' };
  const thStyle = { padding: '0.6rem 0.9rem', textAlign: 'left', fontWeight: 700, color: '#555', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '0.55rem 0.9rem', color: '#333', fontSize: '0.88rem', verticalAlign: 'middle' };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 2.5rem 3rem' }}>
      <h5 style={{ color: '#555', marginBottom: '1.25rem', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.07em', scrollMarginTop: '160px' }}>
        Analytics &amp; Price Optimization
      </h5>

      {/* Metric selector */}
      <div style={{ ...card, marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 340px' }}>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#444', marginBottom: '0.4rem' }}>
              Select Metric
            </label>
            <select
              value={metric}
              onChange={e => { setMetric(e.target.value); setData(null); setError(''); }}
              style={{ width: '100%', padding: '0.6rem 0.85rem', border: '1px solid #ccc', borderRadius: 7, fontSize: '0.92rem', background: '#fafafa' }}
            >
              <option value="">— Choose a metric —</option>
              {METRICS.map(group => (
                <optgroup key={group.category} label={group.category}>
                  {group.items.map(item => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <button onClick={load} disabled={!metric || loading} style={{ ...btnPrimary, opacity: (!metric || loading) ? 0.5 : 1 }}>
            {loading ? 'Loading…' : 'Load Data'}
          </button>
        </div>
        {error && <div style={{ marginTop: '0.75rem', color: '#c00', fontSize: '0.85rem' }}>{error}</div>}
      </div>

      {/* Results table */}
      {data && (
        <div style={card}>
          <div style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.82rem', color: '#888' }}>{rows.length} row{rows.length !== 1 ? 's' : ''}</span>
            {data.higherIsBad && <span style={{ fontSize: '0.78rem', color: '#c00', fontWeight: 600 }}>Higher = worse</span>}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e8eeff', background: '#f8f9fd' }}>
                  {data.columns.map((col, i) => (
                    <th key={i} style={thStyle} onClick={() => toggleSort(i)}>
                      {col}
                      {sortCol === i ? (sortAsc ? ' ▲' : ' ▼') : ' ⇅'}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => {
                  const rawRow = data.raw[ri] || {};
                  const barVal = barIdx !== null ? (parseFloat(rawRow[data.barKey]) || 0) : 0;
                  const barPct = maxVal > 0 ? (barVal / maxVal) * 100 : 0;
                  const barColor = data.higherIsBad ? '#e57373' : '#0d3165';

                  return (
                    <tr key={ri} style={{ borderBottom: '1px solid #f0f0f0', background: ri % 2 === 0 ? '#fff' : '#fafbff' }}>
                      {row.map((cell, ci) => (
                        <td key={ci} style={tdStyle}>
                          {barIdx !== null && ci === barIdx ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <div style={{ flex: '0 0 120px', height: 10, background: '#eee', borderRadius: 5, overflow: 'hidden' }}>
                                <div style={{ width: `${barPct}%`, height: '100%', background: barColor, borderRadius: 5, transition: 'width 0.3s' }} />
                              </div>
                              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>{cell}</span>
                            </div>
                          ) : (
                            cell
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
